import L from "leaflet";
import parseGeoraster from "georaster";
import GeoRasterLayer from "georaster-layer-for-leaflet";

const STAC_SEARCH_URL = "https://earth-search.aws.element84.com/v1/search";
const STAC_COLLECTIONS = [
  { id: "sentinel-2-c1-l2a", name: "Sentinel-2 L2A COG" },
  { id: "landsat-c2-l2", name: "Landsat Collection 2 L2" },
];

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function clamp01(v) {
  return Math.max(0, Math.min(1, Number(v) || 0));
}

function toRasterCrsCode(georaster) {
  const raw = georaster?.projection;
  if (typeof raw === "number" && Number.isFinite(raw)) return `EPSG:${Math.round(raw)}`;
  const code = String(raw || "").trim().toUpperCase();
  if (!code) return "EPSG:4326";
  if (code.startsWith("EPSG:")) return code;
  if (/^\d+$/.test(code)) return `EPSG:${code}`;
  return code;
}

function transformToRasterCoord(app, lon, lat, rasterCrs) {
  if (rasterCrs === "EPSG:4326") return [lon, lat];
  return app.services.projections.transformCoord([lon, lat], "EPSG:4326", rasterCrs);
}

function georasterBoundsLatLng(app, georaster) {
  const rasterCrs = toRasterCrsCode(georaster);
  const corners = [
    [georaster.xmin, georaster.ymin],
    [georaster.xmin, georaster.ymax],
    [georaster.xmax, georaster.ymin],
    [georaster.xmax, georaster.ymax],
  ];
  const ll = corners.map(([x, y]) => {
    const out = rasterCrs === "EPSG:4326"
      ? [x, y]
      : app.services.projections.transformCoord([x, y], rasterCrs, "EPSG:4326");
    return L.latLng(Number(out[1]), Number(out[0]));
  });
  return L.latLngBounds(ll);
}

function combineBounds(boundsList) {
  const valid = boundsList.filter((b) => b?.isValid?.());
  if (!valid.length) return null;
  let out = valid[0];
  for (let i = 1; i < valid.length; i += 1) out = out.extend(valid[i]);
  return out;
}

function sampleBandsAtLatLon(app, georaster, lon, lat) {
  const values = georaster?.values;
  if (!Array.isArray(values) || !values.length) return null;
  const rasterCrs = toRasterCrsCode(georaster);
  let xy;
  try {
    xy = transformToRasterCoord(app, lon, lat, rasterCrs);
  } catch {
    return null;
  }
  const x = Number(xy?.[0]);
  const y = Number(xy?.[1]);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  const pixelWidth = Math.abs(Number(georaster.pixelWidth) || 0);
  const pixelHeight = Math.abs(Number(georaster.pixelHeight) || 0);
  if (!(pixelWidth > 0) || !(pixelHeight > 0)) return null;
  const col = Math.floor((x - Number(georaster.xmin)) / pixelWidth);
  const row = Math.floor((Number(georaster.ymax) - y) / pixelHeight);
  const width = Math.max(0, Number(georaster.width) || 0);
  const height = Math.max(0, Number(georaster.height) || 0);
  if (col < 0 || row < 0 || col >= width || row >= height) return null;
  const nodata = Number(georaster.noDataValue);
  const out = [];
  for (let i = 0; i < values.length; i += 1) {
    const band = values[i];
    const v = band?.[row]?.[col];
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    if (Number.isFinite(nodata) && n === nodata) return null;
    out.push(n);
  }
  return out;
}

function sampleRasterStateAtLatLon(app, rasterState, lon, lat) {
  const sources = Array.isArray(rasterState?.sources) ? rasterState.sources : [];
  if (!sources.length) return null;
  const out = [];
  for (let i = 0; i < sources.length; i += 1) {
    const sample = sampleBandsAtLatLon(app, sources[i].georaster, lon, lat);
    if (!sample) return null;
    for (let j = 0; j < sample.length; j += 1) out.push(sample[j]);
  }
  return out;
}

function solveLinearSystem(matrix, vector) {
  const n = matrix.length;
  const a = matrix.map((row, i) => [...row, vector[i]]);
  for (let col = 0; col < n; col += 1) {
    let pivot = col;
    for (let row = col + 1; row < n; row += 1) {
      if (Math.abs(a[row][col]) > Math.abs(a[pivot][col])) pivot = row;
    }
    if (Math.abs(a[pivot][col]) < 1e-12) return null;
    if (pivot !== col) [a[col], a[pivot]] = [a[pivot], a[col]];
    const pivotVal = a[col][col];
    for (let j = col; j <= n; j += 1) a[col][j] /= pivotVal;
    for (let row = 0; row < n; row += 1) {
      if (row === col) continue;
      const factor = a[row][col];
      if (Math.abs(factor) < 1e-12) continue;
      for (let j = col; j <= n; j += 1) a[row][j] -= factor * a[col][j];
    }
  }
  return a.map((row) => row[n]);
}

function fitLinearRegression(rows, featureFns) {
  const dim = featureFns.length + 1;
  const xtx = Array.from({ length: dim }, () => Array(dim).fill(0));
  const xty = Array(dim).fill(0);
  for (let i = 0; i < rows.length; i += 1) {
    const features = [1, ...featureFns.map((fn) => Number(fn(rows[i])) || 0)];
    const y = Number(rows[i].target) || 0;
    for (let r = 0; r < dim; r += 1) {
      xty[r] += features[r] * y;
      for (let c = 0; c < dim; c += 1) xtx[r][c] += features[r] * features[c];
    }
  }
  return solveLinearSystem(xtx, xty);
}

function calcMetrics(rows, predict) {
  if (!rows.length) {
    return { count: 0, rmse: 0, mae: 0, bias: 0, r2: 0 };
  }
  let sumAbs = 0;
  let sumSq = 0;
  let sumErr = 0;
  let sumY = 0;
  const preds = [];
  for (let i = 0; i < rows.length; i += 1) {
    const y = Number(rows[i].target) || 0;
    const p = Number(predict(rows[i])) || 0;
    const e = p - y;
    preds.push({ p, y, e, row: rows[i] });
    sumY += y;
    sumAbs += Math.abs(e);
    sumSq += e * e;
    sumErr += e;
  }
  const meanY = sumY / rows.length;
  let ssTot = 0;
  for (let i = 0; i < preds.length; i += 1) {
    const dy = preds[i].y - meanY;
    ssTot += dy * dy;
  }
  return {
    count: rows.length,
    rmse: Math.sqrt(sumSq / rows.length),
    mae: sumAbs / rows.length,
    bias: sumErr / rows.length,
    r2: ssTot > 0 ? (1 - (sumSq / ssTot)) : 0,
    predictions: preds,
  };
}

function splitRows(rows, holdoutPct) {
  const pct = Math.max(0.05, Math.min(0.6, Number(holdoutPct) || 0.25));
  const every = Math.max(2, Math.round(1 / pct));
  const training = [];
  const validation = [];
  for (let i = 0; i < rows.length; i += 1) {
    if ((i % every) === every - 1) validation.push(rows[i]);
    else training.push(rows[i]);
  }
  if (!training.length || !validation.length) {
    const mid = Math.max(1, Math.floor(rows.length * (1 - pct)));
    return { training: rows.slice(0, mid), validation: rows.slice(mid) };
  }
  return { training, validation };
}

function residualColor(residual, maxAbs) {
  const m = Math.max(0.1, Number(maxAbs) || 1);
  const t = clamp01((Number(residual) + m) / (m * 2));
  const r = Math.round(255 * t);
  const g = Math.round(120 * (1 - Math.abs(t - 0.5) * 2));
  const b = Math.round(255 * (1 - t));
  return `rgb(${r},${g},${b})`;
}

export const satelliteDerivedBathymetryTool = {
  id: "satellite-derived-bathymetry",
  name: "Satellite Bathymetry",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy(); this._rt = null; },
  renderPanel(app, containerEl) { if (!this._rt) this._rt = createRuntime(app); this._rt.mount(containerEl); },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) { if (!this._rt) this._rt = createRuntime(app); this._rt.restore?.(data); },
};

function createRuntime(app) {
  const map = app.map.instance;
  const residualPane = map.createPane("sdbResidualsPane");
  residualPane.style.zIndex = "812";
  const residualLayer = L.featureGroup([], { pane: "sdbResidualsPane" }).addTo(map);

  let container = null;
  let rasterState = null;
  let overlayGeoTiffLayers = [];
  let overlaySelection = [];
  let stacCollectionId = "sentinel-2-c1-l2a";
  let stacStartDate = "";
  let stacEndDate = "";
  let stacCloudMax = 20;
  let stacBbox = "";
  let stacResults = [];
  let stacSelection = [];
  let stacSearching = false;
  let stacPreviewLayer = null;
  let stacPreviewManagedId = null;
  let clearingStacPreview = false;
  let soundingCatalog = [];
  let soundingLayerId = "";
  let blueBand = 1;
  let greenBand = 2;
  let redBand = 3;
  let holdoutPct = 0.25;
  let modelId = "stumpf";
  let addPreviewLayer = true;
  let previewLayerId = null;
  let residualGlobalId = null;
  let summaryHtml = "Import a local multiband GeoTIFF and select a sounding layer to calibrate a shallow-water SDB model.";
  let diagnosticsHtml = "";
  let processing = false;
  let inGlobalSync = false;
  let offCatalog = null;
  let offLayers = null;
  const el = {};

  function $(id) { return container?.querySelector?.(`#${id}`) || null; }
  function setSummary(html) { summaryHtml = html; if (el.summary) el.summary.innerHTML = html; }
  function setDiagnostics(html) { diagnosticsHtml = html; if (el.diagnostics) el.diagnostics.innerHTML = html; }

  function getSelectedSoundingLayer() {
    return soundingCatalog.find((l) => l.layerId === soundingLayerId || l.globalLayerId === soundingLayerId) || null;
  }

  function registerResidualLayer() {
    if (residualGlobalId) return;
    residualGlobalId = app.services.layers.addLayer("satellite-derived-bathymetry", {
      name: "SDB Residuals",
      kind: "overlay",
      group: "Satellite Bathymetry",
      type: "managed",
      visible: true,
      opacity: 1,
      volatile: true,
      meta: { featureCount: 0 },
      managed: {
        setVisible(v) {
          if (v) {
            if (!map.hasLayer(residualLayer)) residualLayer.addTo(map);
          } else if (map.hasLayer(residualLayer)) map.removeLayer(residualLayer);
        },
        setOpacity(v) {
          const alpha = clamp01(v);
          residualLayer.eachLayer((layer) => layer.setStyle?.({ opacity: alpha, fillOpacity: alpha * 0.85 }));
        },
        getBounds() {
          const b = residualLayer.getBounds?.();
          return b?.isValid?.() ? b : null;
        },
        bringToFront() { residualLayer.bringToFront?.(); },
        remove() {
          if (inGlobalSync) return;
          inGlobalSync = true;
          try {
            residualLayer.clearLayers();
            syncResidualLayerMeta();
            render();
          } finally {
            inGlobalSync = false;
          }
        },
      },
    });
  }

  function syncResidualLayerMeta() {
    if (!residualGlobalId) return;
    app.services.layers.updateLayer(residualGlobalId, {
      meta: { featureCount: residualLayer.getLayers().length },
    });
  }

  function clearResiduals() {
    residualLayer.clearLayers();
    syncResidualLayerMeta();
  }

  function clearStacPreviewLayers() {
    if (clearingStacPreview) return;
    clearingStacPreview = true;
    if (stacPreviewManagedId) {
      try { app.services.layers.removeLayer(stacPreviewManagedId); } catch {}
      stacPreviewManagedId = null;
    }
    if (stacPreviewLayer) {
      try { map.removeLayer(stacPreviewLayer); } catch {}
      stacPreviewLayer = null;
    }
    clearingStacPreview = false;
  }

  function getMapBoundsBboxString() {
    const b = map.getBounds?.();
    if (!b?.isValid?.()) return "";
    return [
      b.getWest().toFixed(6),
      b.getSouth().toFixed(6),
      b.getEast().toFixed(6),
      b.getNorth().toFixed(6),
    ].join(",");
  }

  function parseBbox(text) {
    const parts = String(text || "").split(",").map((part) => Number(part.trim()));
    if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) {
      throw new Error("BBox must be west,south,east,north.");
    }
    if (parts[0] >= parts[2] || parts[1] >= parts[3]) {
      throw new Error("BBox must have west < east and south < north.");
    }
    return parts;
  }

  function normalizeStacAssets(item) {
    const assets = item?.assets && typeof item.assets === "object" ? item.assets : {};
    return Object.entries(assets)
      .map(([key, asset]) => ({ key, ...(asset || {}) }))
      .filter((asset) => /^https?:\/\//i.test(String(asset.href || "")) && /\.tif(f)?($|\?)/i.test(String(asset.href || "")));
  }

  function formatStacResult(item) {
    const props = item?.properties || {};
    const assets = normalizeStacAssets(item);
    return {
      id: String(item?.id || ""),
      collection: String(item?.collection || ""),
      title: String(props.title || item?.id || "Scene"),
      datetime: String(props.datetime || props.start_datetime || "").slice(0, 10),
      cloudCover: Number(props["eo:cloud_cover"]),
      assets,
    };
  }

  async function searchStacScenes() {
    let bbox;
    try {
      bbox = parseBbox(stacBbox || "");
    } catch (error) {
      setSummary(error?.message || "Invalid STAC bbox.");
      render();
      return;
    }
    if (!stacStartDate || !stacEndDate) {
      setSummary("Set STAC start and end dates before searching.");
      render();
      return;
    }
    stacSearching = true;
    stacResults = [];
    stacSelection = [];
    setSummary("Searching public STAC scenes...");
    render();
    try {
      const response = await fetch(STAC_SEARCH_URL, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          collections: [stacCollectionId],
          bbox,
          datetime: `${stacStartDate}T00:00:00Z/${stacEndDate}T23:59:59Z`,
          limit: 12,
          sortby: [{ field: "properties.datetime", direction: "desc" }],
          query: {
            "eo:cloud_cover": { lte: Math.max(0, Math.min(100, Number(stacCloudMax) || 20)) },
          },
        }),
      });
      if (!response.ok) throw new Error(`STAC search failed: HTTP ${response.status}`);
      const payload = await response.json();
      const features = Array.isArray(payload?.features) ? payload.features : [];
      stacResults = features.map(formatStacResult).filter((item) => item.assets.length);
      setSummary(
        stacResults.length
          ? `Found ${stacResults.length} STAC scene${stacResults.length === 1 ? "" : "s"} with direct GeoTIFF assets.`
          : "No STAC scenes with direct GeoTIFF assets matched the search.",
      );
    } catch (error) {
      console.error("[SDB] STAC search failed", error);
      setSummary(`STAC search failed: ${esc(error?.message || String(error))}`);
      stacResults = [];
      stacSelection = [];
    } finally {
      stacSearching = false;
      render();
    }
  }

  function toggleStacAssetSelection(sceneId, assetKey, checked) {
    const token = `${sceneId}::${assetKey}`;
    if (checked) {
      if (!stacSelection.includes(token)) stacSelection = [...stacSelection, token];
    } else {
      stacSelection = stacSelection.filter((value) => value !== token);
    }
  }

  function refreshOverlayGeoTiffLayers() {
    overlayGeoTiffLayers = app.services.layers.listLayers()
      .filter((layer) => layer.toolId === "overlay-manager" && layer.type === "geotiff-url" && typeof layer.url === "string" && layer.url)
      .map((layer) => ({ ...layer }));
    overlaySelection = overlaySelection.filter((id) => overlayGeoTiffLayers.some((layer) => layer.id === id));
  }

  function clampBandSelectors() {
    const count = Math.max(1, Number(rasterState?.bandCount || 1));
    blueBand = Math.min(Math.max(1, blueBand), count);
    greenBand = Math.min(Math.max(1, greenBand), count);
    redBand = Math.min(Math.max(1, redBand), count);
  }

  function setRasterState(nextState) {
    rasterState = nextState;
    clampBandSelectors();
  }

  async function importRaster(file) {
    processing = true;
    render();
    try {
      const buf = await file.arrayBuffer();
      const georaster = await parseGeoraster(buf);
      const bounds = georasterBoundsLatLng(app, georaster);
      setRasterState({
        fileName: file.name,
        fileSize: Number(file.size) || 0,
        rasterCrsSummary: toRasterCrsCode(georaster),
        bandCount: Math.max(1, Number(georaster.numberOfRasters || georaster.values?.length || 1)),
        bounds,
        sourceMode: "local-file",
        sources: [{
          id: "local-file",
          name: file.name,
          georaster,
          bandCount: Math.max(1, Number(georaster.numberOfRasters || georaster.values?.length || 1)),
        }],
      });
      if (addPreviewLayer) {
        if (previewLayerId) {
          try { app.services.layers.removeLayer(previewLayerId); } catch {}
        }
        const blobUrl = URL.createObjectURL(file);
        previewLayerId = app.services.layers.addLayer("satellite-derived-bathymetry", {
          id: `sdb-preview-${Math.random().toString(16).slice(2, 8)}`,
          name: `SDB Preview: ${file.name}`,
          kind: "overlay",
          type: "geotiff-url",
          url: blobUrl,
          options: { resolution: 256 },
          opacity: 0.8,
          visible: true,
          volatile: true,
          meta: {
            sourceType: "local-file",
            fileName: file.name,
            fileSize: Number(file.size) || 0,
            sdbPreview: true,
          },
        });
      }
      if (bounds?.isValid?.()) map.fitBounds(bounds.pad(0.08));
      setSummary(`Loaded raster <strong>${esc(file.name)}</strong> with ${rasterState.bandCount} band(s) in ${esc(rasterState.rasterCrsSummary)}.`);
      setDiagnostics("");
    } catch (error) {
      console.error("[SDB] raster import failed", error);
      setRasterState(null);
      setSummary(`Raster import failed: ${esc(error?.message || String(error))}`);
      setDiagnostics("");
    } finally {
      processing = false;
      render();
    }
  }

  async function loadRasterFromOverlaySelection() {
    if (!overlaySelection.length) {
      setSummary("Select one or more GeoTIFF overlay layers first.");
      return;
    }
    processing = true;
    render();
    try {
      const chosen = overlayGeoTiffLayers.filter((layer) => overlaySelection.includes(layer.id));
      if (!chosen.length) throw new Error("Selected overlay layers are no longer available.");
      const sources = [];
      for (let i = 0; i < chosen.length; i += 1) {
        const layer = chosen[i];
        const response = await fetch(layer.url);
        if (!response.ok) throw new Error(`Failed to fetch ${layer.name}: HTTP ${response.status}`);
        const georaster = await parseGeoraster(await response.arrayBuffer());
        sources.push({
          id: layer.id,
          name: layer.name,
          georaster,
          bandCount: Math.max(1, Number(georaster.numberOfRasters || georaster.values?.length || 1)),
        });
      }
      const bandCount = sources.reduce((sum, source) => sum + source.bandCount, 0);
      const bounds = combineBounds(sources.map((source) => georasterBoundsLatLng(app, source.georaster)));
      const crsSummary = [...new Set(sources.map((source) => toRasterCrsCode(source.georaster)))].join(", ");
      setRasterState({
        fileName: chosen.map((layer) => layer.name).join(" + "),
        fileSize: 0,
        rasterCrsSummary: crsSummary,
        bandCount,
        bounds,
        sourceMode: "overlay-stack",
        sources,
      });
      if (bounds?.isValid?.()) map.fitBounds(bounds.pad(0.08));
      setSummary(`Loaded ${chosen.length} overlay layer${chosen.length === 1 ? "" : "s"} as an SDB raster stack with ${bandCount} total band${bandCount === 1 ? "" : "s"}.`);
      setDiagnostics("");
    } catch (error) {
      console.error("[SDB] overlay stack load failed", error);
      setSummary(`Overlay stack load failed: ${esc(error?.message || String(error))}`);
      setDiagnostics("");
    } finally {
      processing = false;
      render();
    }
  }

  async function loadRasterFromStacSelection() {
    if (!stacSelection.length) {
      setSummary("Select one or more STAC assets first.");
      return;
    }
    processing = true;
    render();
    try {
      const chosenAssets = [];
      for (let i = 0; i < stacResults.length; i += 1) {
        const scene = stacResults[i];
        for (let j = 0; j < scene.assets.length; j += 1) {
          const asset = scene.assets[j];
          const token = `${scene.id}::${asset.key}`;
          if (!stacSelection.includes(token)) continue;
          chosenAssets.push({
            id: token,
            name: `${scene.collection}:${scene.id}:${asset.title || asset.key}`,
            href: String(asset.href || ""),
          });
        }
      }
      if (!chosenAssets.length) throw new Error("Selected STAC assets are no longer available.");
      const sources = [];
      for (let i = 0; i < chosenAssets.length; i += 1) {
        const asset = chosenAssets[i];
        const response = await fetch(asset.href);
        if (!response.ok) throw new Error(`Failed to fetch ${asset.name}: HTTP ${response.status}`);
        const georaster = await parseGeoraster(await response.arrayBuffer());
        sources.push({
          id: asset.id,
          name: asset.name,
          georaster,
          bandCount: Math.max(1, Number(georaster.numberOfRasters || georaster.values?.length || 1)),
        });
      }
      const bandCount = sources.reduce((sum, source) => sum + source.bandCount, 0);
      const bounds = combineBounds(sources.map((source) => georasterBoundsLatLng(app, source.georaster)));
      const crsSummary = [...new Set(sources.map((source) => toRasterCrsCode(source.georaster)))].join(", ");
      setRasterState({
        fileName: chosenAssets.map((asset) => asset.name).join(" + "),
        fileSize: 0,
        rasterCrsSummary: crsSummary,
        bandCount,
        bounds,
        sourceMode: "stac-stack",
        sources,
      });
      if (bounds?.isValid?.()) map.fitBounds(bounds.pad(0.08));
      setSummary(`Loaded ${chosenAssets.length} STAC asset${chosenAssets.length === 1 ? "" : "s"} as an SDB raster stack with ${bandCount} total band${bandCount === 1 ? "" : "s"}.`);
      setDiagnostics("");
    } catch (error) {
      console.error("[SDB] STAC stack load failed", error);
      setSummary(`STAC stack load failed: ${esc(error?.message || String(error))}`);
      setDiagnostics("");
    } finally {
      processing = false;
      render();
    }
  }

  function getSelectedStacAssets() {
    const out = [];
    for (let i = 0; i < stacResults.length; i += 1) {
      const scene = stacResults[i];
      for (let j = 0; j < scene.assets.length; j += 1) {
        const asset = scene.assets[j];
        const token = `${scene.id}::${asset.key}`;
        if (!stacSelection.includes(token)) continue;
        out.push({
          token,
          sceneId: scene.id,
          collection: scene.collection,
          sceneTitle: scene.title,
          assetKey: asset.key,
          assetTitle: String(asset.title || asset.key || asset.key),
          href: String(asset.href || ""),
        });
      }
    }
    return out;
  }

  function previewSelectedStacAssets() {
    const assets = getSelectedStacAssets();
    if (!assets.length) {
      setSummary("Select one or more STAC assets to preview.");
      render();
      return;
    }
    clearStacPreviewLayers();
    processing = true;
    setSummary("Building STAC preview layer...");
    render();
    (async () => {
      try {
        const previewAssets = assets.slice(0, 3);
        const georasters = [];
        for (let i = 0; i < previewAssets.length; i += 1) {
          const response = await fetch(previewAssets[i].href);
          if (!response.ok) throw new Error(`Failed to fetch ${previewAssets[i].assetTitle}: HTTP ${response.status}`);
          const georaster = await parseGeoraster(await response.arrayBuffer());
          georasters.push(georaster);
        }
        if (!georasters.length) throw new Error("No STAC assets could be prepared for preview.");
        const layer = new GeoRasterLayer({
          georasters,
          opacity: 0.85,
          resolution: 128,
        });
        stacPreviewLayer = layer;
        if (!map.hasLayer(layer)) layer.addTo(map);
        const bounds = layer.getBounds?.();
        stacPreviewManagedId = app.services.layers.addLayer("satellite-derived-bathymetry", {
          id: `sdb-stac-preview-${Math.random().toString(16).slice(2, 8)}`,
          name: `SDB STAC Preview (${previewAssets.length} asset${previewAssets.length === 1 ? "" : "s"})`,
          kind: "overlay",
          group: "Satellite Bathymetry",
          type: "managed",
          visible: true,
          opacity: 0.85,
          volatile: true,
          meta: {
            sourceType: "stac-preview",
            featureCount: previewAssets.length,
          },
          managed: {
            setVisible(v) {
              if (v) {
                if (!map.hasLayer(layer)) layer.addTo(map);
              } else if (map.hasLayer(layer)) map.removeLayer(layer);
            },
            setOpacity(v) {
              layer.setOpacity?.(clamp01(v));
            },
            getBounds() {
              return bounds?.isValid?.() ? bounds : layer.getBounds?.();
            },
            bringToFront() { layer.bringToFront?.(); },
            remove() {
              if (stacPreviewLayer) {
                try { map.removeLayer(stacPreviewLayer); } catch {}
              }
              stacPreviewLayer = null;
              stacPreviewManagedId = null;
              render();
            },
          },
        });
        if (bounds?.isValid?.()) map.fitBounds(bounds.pad(0.08));
        setSummary(`Previewed ${previewAssets.length} selected STAC asset${previewAssets.length === 1 ? "" : "s"} on the map${assets.length > 3 ? " (first 3 assets used for preview)" : ""}.`);
      } catch (error) {
        console.error("[SDB] STAC preview build failed", error);
        setSummary(`STAC preview failed: ${esc(error?.message || String(error))}`);
      } finally {
        processing = false;
        render();
      }
    })();
  }

  function getBandValue(bands, oneBasedIndex) {
    const idx = Math.max(1, Number(oneBasedIndex) || 1) - 1;
    return Number(bands?.[idx]);
  }

  function buildTrainingRows(records) {
    const rows = [];
    for (let i = 0; i < records.length; i += 1) {
      const r = records[i];
      const bands = sampleRasterStateAtLatLon(app, rasterState, Number(r.lon), Number(r.lat));
      if (!bands) continue;
      const blue = getBandValue(bands, blueBand);
      const green = getBandValue(bands, greenBand);
      const red = getBandValue(bands, redBand);
      if (![blue, green, red].every((v) => Number.isFinite(v))) continue;
      if (!(blue > 0) || !(green > 0)) continue;
      rows.push({
        target: Number(r.z ?? r.depth_m) || 0,
        lat: Number(r.lat),
        lon: Number(r.lon),
        blue,
        green,
        red,
      });
    }
    return rows;
  }

  function buildModel(rows, selectedModel) {
    if (selectedModel === "lyzenga") {
      const coeffs = fitLinearRegression(rows, [
        (row) => Math.log(row.blue + 1),
        (row) => Math.log(row.green + 1),
        (row) => Math.log(row.red + 1),
      ]);
      if (!coeffs) return null;
      return {
        id: "lyzenga",
        label: "Lyzenga-style Linear",
        coeffs,
        predict(row) {
          return coeffs[0]
            + (coeffs[1] * Math.log(row.blue + 1))
            + (coeffs[2] * Math.log(row.green + 1))
            + (coeffs[3] * Math.log(row.red + 1));
        },
        equation: `z = ${coeffs[0].toFixed(3)} + ${coeffs[1].toFixed(3)} ln(B) + ${coeffs[2].toFixed(3)} ln(G) + ${coeffs[3].toFixed(3)} ln(R)`,
      };
    }
    const coeffs = fitLinearRegression(rows, [
      (row) => Math.log(row.blue + 1) - Math.log(row.green + 1),
    ]);
    if (!coeffs) return null;
    return {
      id: "stumpf",
      label: "Band Ratio",
      coeffs,
      predict(row) {
        return coeffs[0] + (coeffs[1] * (Math.log(row.blue + 1) - Math.log(row.green + 1)));
      },
      equation: `z = ${coeffs[0].toFixed(3)} + ${coeffs[1].toFixed(3)} [ln(B) - ln(G)]`,
    };
  }

  function renderResiduals(metricRows) {
    clearResiduals();
    if (!metricRows.length) return;
    const maxAbs = metricRows.reduce((m, item) => Math.max(m, Math.abs(Number(item.e) || 0)), 0);
    metricRows.forEach((item) => {
      const row = item.row;
      const residual = Number(item.e) || 0;
      const marker = L.circleMarker([row.lat, row.lon], {
        radius: 4,
        color: "#0f172a",
        weight: 1,
        opacity: 0.9,
        fillOpacity: 0.85,
        fillColor: residualColor(residual, maxAbs),
      });
      marker.bindTooltip(
        `Obs ${row.target.toFixed(2)} m | Pred ${item.p.toFixed(2)} m | Resid ${residual.toFixed(2)} m`,
        { sticky: true },
      );
      marker.addTo(residualLayer);
    });
    registerResidualLayer();
    syncResidualLayerMeta();
  }

  function analyze() {
    if (!rasterState?.sources?.length) {
      setSummary("Load a local GeoTIFF or selected overlay stack first.");
      return;
    }
    const soundingLayer = getSelectedSoundingLayer();
    const records = Array.isArray(soundingLayer?.records) ? soundingLayer.records : [];
    if (!records.length) {
      setSummary("Select a sounding layer with records.");
      return;
    }
    const rows = buildTrainingRows(records);
    if (rows.length < 12) {
      setSummary(`Only ${rows.length} coincident samples were found. Load a larger raster overlap or a denser sounding layer.`);
      setDiagnostics("");
      clearResiduals();
      return;
    }
    const split = splitRows(rows, holdoutPct);
    const model = buildModel(split.training, modelId);
    if (!model) {
      setSummary("Model fit failed. Check band selection and raster values.");
      setDiagnostics("");
      clearResiduals();
      return;
    }
    const trainMetrics = calcMetrics(split.training, model.predict);
    const valMetrics = calcMetrics(split.validation, model.predict);
    renderResiduals(valMetrics.predictions || []);
    setSummary(
      `Calibrated <strong>${esc(model.label)}</strong> using ${trainMetrics.count.toLocaleString()} training points and ${valMetrics.count.toLocaleString()} validation points from <strong>${esc(soundingLayer.name || "soundings")}</strong>.`,
    );
    setDiagnostics(`
      <div><strong>Equation</strong>: ${esc(model.equation)}</div>
      <div style="margin-top:6px;"><strong>Raster</strong>: ${esc(rasterState.fileName)} (${rasterState.bandCount} band${rasterState.bandCount === 1 ? "" : "s"}, ${esc(rasterState.sourceMode)})</div>
      <div><strong>Overlap samples</strong>: ${rows.length.toLocaleString()}</div>
      <div style="margin-top:8px;"><strong>Training</strong>: RMSE ${trainMetrics.rmse.toFixed(2)} m, MAE ${trainMetrics.mae.toFixed(2)} m, Bias ${trainMetrics.bias.toFixed(2)} m, R² ${trainMetrics.r2.toFixed(3)}</div>
      <div><strong>Validation</strong>: RMSE ${valMetrics.rmse.toFixed(2)} m, MAE ${valMetrics.mae.toFixed(2)} m, Bias ${valMetrics.bias.toFixed(2)} m, R² ${valMetrics.r2.toFixed(3)}</div>
      <div style="margin-top:8px;" class="small">Current MVP fits from sounding/raster intersections and publishes validation residuals to the map. It does not yet emit a derived bathymetry raster.</div>
    `);
    render();
  }

  function soundingOptionsHtml() {
    return soundingCatalog
      .map((layer) => `<option value="${esc(layer.globalLayerId || layer.layerId)}" ${(layer.globalLayerId || layer.layerId) === soundingLayerId ? "selected" : ""}>${esc(layer.name)} (${Number(layer.featureCount || layer.records?.length || 0).toLocaleString()})</option>`)
      .join("");
  }

  function bandOptionsHtml(selected) {
    const count = Math.max(1, Number(rasterState?.bandCount || 1));
    return Array.from({ length: count }, (_, i) => i + 1)
      .map((band) => `<option value="${band}" ${band === Number(selected) ? "selected" : ""}>Band ${band}</option>`)
      .join("");
  }

  function stacCollectionOptionsHtml() {
    return STAC_COLLECTIONS
      .map((collection) => `<option value="${collection.id}" ${collection.id === stacCollectionId ? "selected" : ""}>${esc(collection.name)}</option>`)
      .join("");
  }

  function stacResultsHtml() {
    if (!stacResults.length) return '<div class="small">No STAC scenes loaded.</div>';
    return stacResults.map((scene) => `
      <div class="card" style="padding:8px; margin-top:6px;">
        <div style="font-weight:700;">${esc(scene.title)}</div>
        <div class="small">${esc(scene.collection)} | ${esc(scene.datetime || "unknown")} | ${Number.isFinite(scene.cloudCover) ? `${scene.cloudCover.toFixed(1)}% cloud` : "cloud n/a"}</div>
        <div style="margin-top:6px;">
          ${scene.assets.map((asset) => {
            const token = `${scene.id}::${asset.key}`;
            return `
              <label class="small" style="display:flex; gap:8px; align-items:flex-start; margin-bottom:6px;">
                <input type="checkbox" data-sdb-stac-token="${esc(token)}" ${stacSelection.includes(token) ? "checked" : ""} />
                <span>${esc(asset.title || asset.key || asset.key)}</span>
              </label>
            `;
          }).join("")}
        </div>
      </div>
    `).join("");
  }

  function stacSelectionSummaryHtml() {
    const assets = getSelectedStacAssets();
    if (!assets.length) return '<div class="small">No STAC assets selected yet.</div>';
    return `
      <div class="small"><strong>${assets.length}</strong> STAC asset${assets.length === 1 ? "" : "s"} selected.</div>
      <div class="small" style="margin-top:4px;">${assets.map((asset) => esc(asset.assetTitle)).join(", ")}</div>
    `;
  }

  function render() {
    if (!container) return;
    container.innerHTML = `
      <div class="row" style="gap:8px; align-items:center; margin-bottom:8px;">
        <button id="sdb-import" class="btn" ${processing ? "disabled" : ""}>Import GeoTIFF</button>
        <input id="sdb-file" type="file" accept=".tif,.tiff,image/tiff" style="display:none;" />
        <button id="sdb-load-overlays" class="btn" ${(processing || !overlaySelection.length) ? "disabled" : ""}>Use Overlay Stack</button>
        <button id="sdb-load-stac" class="btn" ${(processing || !stacSelection.length) ? "disabled" : ""}>Use STAC Stack</button>
        <button id="sdb-run" class="btn primary" ${(processing || !rasterState || !soundingCatalog.length) ? "disabled" : ""}>Run Calibration</button>
        <button id="sdb-clear-residuals" class="btn" ${residualLayer.getLayers().length ? "" : "disabled"}>Clear Residuals</button>
      </div>
      <div id="sdb-summary" class="small" style="margin-bottom:8px;">${summaryHtml}</div>
      <div class="card" style="padding:10px; margin-bottom:8px;">
        <div style="font-weight:700; margin-bottom:6px;">Raster</div>
        <div class="small">${rasterState ? `${esc(rasterState.fileName)} | ${rasterState.bandCount} band(s) | ${esc(rasterState.rasterCrsSummary)} | ${esc(rasterState.sourceMode)}` : "No raster loaded."}</div>
        <div class="row" style="gap:8px; margin-top:8px; flex-wrap:wrap;">
          <label class="muted">Blue
            <select id="sdb-blue" ${rasterState ? "" : "disabled"}>${bandOptionsHtml(blueBand)}</select>
          </label>
          <label class="muted">Green
            <select id="sdb-green" ${rasterState ? "" : "disabled"}>${bandOptionsHtml(greenBand)}</select>
          </label>
          <label class="muted">Red
            <select id="sdb-red" ${rasterState ? "" : "disabled"}>${bandOptionsHtml(redBand)}</select>
          </label>
          <label class="muted"><input id="sdb-preview-layer" type="checkbox" ${addPreviewLayer ? "checked" : ""}> Add preview layer</label>
        </div>
        <div style="margin-top:8px; font-weight:700;">Overlay Bridge</div>
        <div class="small" style="margin-top:4px;">Select one or more GeoTIFF layers from Overlay Manager. Their bands will be stacked in the order shown below.</div>
        <div style="margin-top:6px; max-height:150px; overflow:auto; border:1px solid #334155; border-radius:8px; padding:8px;">
          ${overlayGeoTiffLayers.length ? overlayGeoTiffLayers.map((layer) => `
            <label class="small" style="display:flex; gap:8px; align-items:flex-start; margin-bottom:6px;">
              <input type="checkbox" data-sdb-overlay-id="${esc(layer.id)}" ${overlaySelection.includes(layer.id) ? "checked" : ""} />
              <span>${esc(layer.name)}</span>
            </label>
          `).join("") : '<div class="small">No GeoTIFF overlay layers are currently loaded.</div>'}
        </div>
        <div style="margin-top:10px; font-weight:700;">Public STAC Search</div>
        <div class="small" style="margin-top:4px;">Search public Sentinel-2 or Landsat scenes and pull selected GeoTIFF assets directly into the SDB stack.</div>
        <div class="row" style="gap:8px; margin-top:8px; flex-wrap:wrap;">
          <select id="sdb-stac-collection">${stacCollectionOptionsHtml()}</select>
          <input id="sdb-stac-start" type="date" value="${esc(stacStartDate)}" />
          <input id="sdb-stac-end" type="date" value="${esc(stacEndDate)}" />
          <input id="sdb-stac-cloud-max" type="number" min="0" max="100" step="1" value="${esc(stacCloudMax)}" style="width:90px;" />
        </div>
        <div class="row" style="gap:8px; margin-top:8px;">
          <input id="sdb-stac-bbox" type="text" value="${esc(stacBbox)}" placeholder="west,south,east,north" style="flex:1;" />
          <button id="sdb-stac-use-view" class="btn">Use Map View</button>
          <button id="sdb-stac-search" class="btn" ${stacSearching ? "disabled" : ""}>${stacSearching ? "Searching..." : "Search"}</button>
        </div>
        <div style="margin-top:8px; padding:8px; border:1px solid #334155; border-radius:8px;">
          ${stacSelectionSummaryHtml()}
          <div class="row" style="gap:8px; margin-top:8px;">
            <button id="sdb-stac-preview" class="btn" ${(processing || !stacSelection.length) ? "disabled" : ""}>Preview Selected Assets</button>
            <button id="sdb-stac-clear-preview" class="btn" ${stacPreviewManagedId ? "" : "disabled"}>Clear Preview</button>
          </div>
        </div>
        <div style="margin-top:6px; max-height:220px; overflow:auto; border:1px solid #334155; border-radius:8px; padding:8px;">
          ${stacResultsHtml()}
        </div>
      </div>
      <div class="card" style="padding:10px; margin-bottom:8px;">
        <div style="font-weight:700; margin-bottom:6px;">Calibration</div>
        <div class="row" style="gap:8px; flex-wrap:wrap;">
          <label class="muted">Soundings
            <select id="sdb-sounding-layer">${soundingOptionsHtml() || "<option value=\"\">No sounding layers</option>"}</select>
          </label>
          <label class="muted">Model
            <select id="sdb-model">
              <option value="stumpf" ${modelId === "stumpf" ? "selected" : ""}>Band Ratio</option>
              <option value="lyzenga" ${modelId === "lyzenga" ? "selected" : ""}>Linear Log Bands</option>
            </select>
          </label>
          <label class="muted">Holdout
            <input id="sdb-holdout" type="number" min="0.05" max="0.6" step="0.05" value="${holdoutPct}" style="width:70px;" />
          </label>
        </div>
        <div class="small" style="margin-top:8px;">Use existing soundings as control points. The tool samples raster bands at those positions, splits training/validation, then reports fit metrics and residuals.</div>
      </div>
      <div id="sdb-diagnostics" class="small">${diagnosticsHtml || "No diagnostics yet."}</div>
    `;
    el.summary = $("sdb-summary");
    el.diagnostics = $("sdb-diagnostics");
    $("sdb-import")?.addEventListener("click", () => $("sdb-file")?.click());
    $("sdb-file")?.addEventListener("change", async (e) => {
      const file = e.target?.files?.[0];
      e.target.value = "";
      if (!file) return;
      await importRaster(file);
    });
    $("sdb-load-overlays")?.addEventListener("click", loadRasterFromOverlaySelection);
    $("sdb-load-stac")?.addEventListener("click", loadRasterFromStacSelection);
    $("sdb-run")?.addEventListener("click", analyze);
    $("sdb-clear-residuals")?.addEventListener("click", () => {
      clearResiduals();
      setDiagnostics(diagnosticsHtml ? `${diagnosticsHtml}<div style="margin-top:8px;" class="small">Residual layer cleared.</div>` : "Residual layer cleared.");
      render();
    });
    $("sdb-blue")?.addEventListener("change", (e) => { blueBand = Number(e.target.value) || 1; });
    $("sdb-green")?.addEventListener("change", (e) => { greenBand = Number(e.target.value) || 1; });
    $("sdb-red")?.addEventListener("change", (e) => { redBand = Number(e.target.value) || 1; });
    $("sdb-model")?.addEventListener("change", (e) => { modelId = String(e.target.value || "stumpf"); });
    $("sdb-holdout")?.addEventListener("change", (e) => {
      holdoutPct = Math.max(0.05, Math.min(0.6, Number(e.target.value) || 0.25));
      e.target.value = String(holdoutPct);
    });
    $("sdb-preview-layer")?.addEventListener("change", (e) => { addPreviewLayer = !!e.target.checked; });
    $("sdb-sounding-layer")?.addEventListener("change", (e) => { soundingLayerId = String(e.target.value || ""); });
    $("sdb-stac-collection")?.addEventListener("change", (e) => { stacCollectionId = String(e.target.value || "sentinel-2-c1-l2a"); });
    $("sdb-stac-start")?.addEventListener("change", (e) => { stacStartDate = String(e.target.value || ""); });
    $("sdb-stac-end")?.addEventListener("change", (e) => { stacEndDate = String(e.target.value || ""); });
    $("sdb-stac-cloud-max")?.addEventListener("change", (e) => {
      stacCloudMax = Math.max(0, Math.min(100, Number(e.target.value) || 20));
      e.target.value = String(stacCloudMax);
    });
    $("sdb-stac-bbox")?.addEventListener("change", (e) => { stacBbox = String(e.target.value || ""); });
    $("sdb-stac-use-view")?.addEventListener("click", () => {
      stacBbox = getMapBoundsBboxString();
      render();
    });
    $("sdb-stac-search")?.addEventListener("click", searchStacScenes);
    $("sdb-stac-preview")?.addEventListener("click", previewSelectedStacAssets);
    $("sdb-stac-clear-preview")?.addEventListener("click", () => {
      clearStacPreviewLayers();
      setSummary("Cleared STAC preview layers.");
      render();
    });
    container.querySelectorAll("[data-sdb-overlay-id]").forEach((node) => {
      node.addEventListener("change", (e) => {
        const id = String(e.target.getAttribute("data-sdb-overlay-id") || "");
        if (!id) return;
        if (e.target.checked) {
          if (!overlaySelection.includes(id)) overlaySelection = [...overlaySelection, id];
        } else {
          overlaySelection = overlaySelection.filter((value) => value !== id);
        }
        render();
      });
    });
    container.querySelectorAll("[data-sdb-stac-token]").forEach((node) => {
      node.addEventListener("change", (e) => {
        const token = String(e.target.getAttribute("data-sdb-stac-token") || "");
        const parts = token.split("::");
        if (parts.length !== 2) return;
        toggleStacAssetSelection(parts[0], parts[1], !!e.target.checked);
        render();
      });
    });
  }

  function mount(next) {
    if (container === next) return;
    container = next;
    refreshOverlayGeoTiffLayers();
    render();
    registerResidualLayer();
    offCatalog = app.events.on("soundings:catalog:changed", ({ layers } = {}) => {
      soundingCatalog = Array.isArray(layers) ? layers : [];
      if (!soundingLayerId && soundingCatalog[0]) soundingLayerId = String(soundingCatalog[0].globalLayerId || soundingCatalog[0].layerId || "");
      render();
    });
    offLayers = app.events.on("layers:changed", () => {
      refreshOverlayGeoTiffLayers();
      if (previewLayerId && !app.services.layers.listLayers().some((layer) => layer.id === previewLayerId)) {
        previewLayerId = null;
      }
      if (stacPreviewManagedId && !app.services.layers.listLayers().some((layer) => layer.id === stacPreviewManagedId)) {
        stacPreviewManagedId = null;
        stacPreviewLayer = null;
      }
      render();
    });
    app.events.emit("soundings:catalog:request");
  }

  function snapshot() {
    return {
      soundingLayerId,
      blueBand,
      greenBand,
      redBand,
      holdoutPct,
      modelId,
      addPreviewLayer,
      overlaySelection,
      stacCollectionId,
      stacStartDate,
      stacEndDate,
      stacCloudMax,
      stacBbox,
      stacSelection,
    };
  }

  function restore(data) {
    soundingLayerId = String(data?.soundingLayerId || soundingLayerId || "");
    blueBand = Math.max(1, Number(data?.blueBand) || blueBand || 1);
    greenBand = Math.max(1, Number(data?.greenBand) || greenBand || 1);
    redBand = Math.max(1, Number(data?.redBand) || redBand || 1);
    holdoutPct = Math.max(0.05, Math.min(0.6, Number(data?.holdoutPct) || holdoutPct || 0.25));
    modelId = String(data?.modelId || modelId || "stumpf");
    addPreviewLayer = data?.addPreviewLayer !== false;
    overlaySelection = Array.isArray(data?.overlaySelection) ? data.overlaySelection.map((id) => String(id || "")).filter(Boolean) : overlaySelection;
    stacCollectionId = String(data?.stacCollectionId || stacCollectionId || "sentinel-2-c1-l2a");
    stacStartDate = String(data?.stacStartDate || stacStartDate || "");
    stacEndDate = String(data?.stacEndDate || stacEndDate || "");
    stacCloudMax = Math.max(0, Math.min(100, Number(data?.stacCloudMax) || stacCloudMax || 20));
    stacBbox = String(data?.stacBbox || stacBbox || "");
    stacSelection = Array.isArray(data?.stacSelection) ? data.stacSelection.map((id) => String(id || "")).filter(Boolean) : stacSelection;
    render();
  }

  function destroy() {
    try { offCatalog?.(); } catch {}
    try { offLayers?.(); } catch {}
    try { residualLayer.clearLayers(); } catch {}
    try { map.removeLayer(residualLayer); } catch {}
    clearStacPreviewLayers();
    if (previewLayerId) {
      try { app.services.layers.removeLayer(previewLayerId); } catch {}
      previewLayerId = null;
    }
  }

  return {
    mount,
    snapshot,
    restore,
    destroy,
  };
}
